import Vue from 'vue'
import router from '@/router'
import axios from 'axios'

// 创建 axios 实例
const instance = axios.create({
  baseURL: Vue.GLOBAL_ENV.VUE_APP_BASE_DOMAIN, // API 基础路径
  timeout: 5000, // 请求超时时间
  withCredentials: true // 跨域请求时发送 cookies
})

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.params.token = token
    }
    return config
  },
  (error) => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
instance.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 自定义 Promise 扩展类
class ExtendedPromise {
  constructor(promise) {
    this.promise = promise
  }

  // 扩展 then 方法
  asyncThen(successCallback, errorCallback) {
    this.promise = this.promise.then(
      (response) => {
        if (response.rst === 401) {
          localStorage.clear()
          router.push({ name: 'login' })
        }
        if (response.rst === 200) {
          if (!this.apiConfig.disableSuccessMsg) {
            Vue.prototype.$message.closeAll()
            Vue.prototype.$message({
              message: '操作成功',
              type: 'success'
            })
          }
        } else {
          Vue.prototype.$message.closeAll()
          Vue.prototype.$message.error(response.msg)
        }
        return successCallback ? successCallback(response) : response
      },
      (error) => {
        if (!this.apiConfig.disableErrorMsg) {
          Vue.prototype.$message.error(error.message)
        }
        return errorCallback ? errorCallback(error) : Promise.reject(error)
      }
    )
    return this
  }

  // 扩展 catch 方法
  asyncErrorCatch(errorCallback) {
    this.promise = this.promise.catch(error => {
      return errorCallback ? errorCallback(error) : Promise.reject(error)
    })
    return this
  }

  // 等待 Promise 完成
  async wait() {
    return this.promise
  }
}

// 封装请求方法
function createRequest() {
  return {
    /**
     * 发送请求
     * @param {object} requestConfig - 请求配置
     * @returns {ExtendedPromise}
     */
    request(requestConfig) {
      // 解构请求配置
      const { url, method = 'get', params = {}, ...rest } = requestConfig

      // 创建 axios 请求配置
      const axiosConfig = {
        url,
        method,
        ...rest
      }

      // 根据请求方法设置参数
      if (method.toLowerCase() === 'get') {
        axiosConfig.params = {
          token: '',
          ...params
        }
      } else {
        axiosConfig.params = {
          token: ''
        }
        axiosConfig.data = params
      }

      // 发送请求并返回扩展的 Promise
      const promise = instance(axiosConfig)
      const extendedPromise = new ExtendedPromise(promise)

      // 保存 API 配置以便在拦截器中使用
      extendedPromise.apiConfig = requestConfig

      return extendedPromise
    },

    /**
     * 快捷方式：GET 请求
     */
    get(url, params = {}, config = {}) {
      return this.request({ url, method: 'get', params, ...config })
    },

    /**
     * 快捷方式：POST 请求
     */
    post(url, data = {}, config = {}) {
      return this.request({ url, method: 'post', data, ...config })
    },

    /**
     * 快捷方式：PUT 请求
     */
    put(url, data = {}, config = {}) {
      return this.request({ url, method: 'put', data, ...config })
    },

    /**
     * 快捷方式：DELETE 请求
     */
    delete(url, params = {}, config = {}) {
      return this.request({ url, method: 'delete', params, ...config })
    }
  }
}

// 创建请求实例
const requestInstance = createRequest()

// 导出默认实例
export default requestInstance

// 导出扩展的 Promise 类
export { ExtendedPromise }
